/**
 * Custom React hooks for authentication
 * Provides convenient access to auth context and state
 *
 * @module useAuth
 */

import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '../utils/logger'
import { AuthContext, type AuthContextValue } from '../context/AuthContext'
import { ROUTES } from '../utils/constants'

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 * @returns Auth context value with state and methods
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthContextValue {
  logger.debug(`useAuth: accessing auth context`)

  const context = useContext(AuthContext)

  if (!context) {
    logger.error(`useAuth: hook called outside of AuthProvider`)
    throw new Error('useAuth must be used within AuthProvider')
  }

  return context
}

/**
 * Hook to check if current user is admin
 * @returns Boolean indicating admin status
 */
export function useIsAdmin(): boolean {
  logger.debug(`useIsAdmin: checking if current user is admin`)

  const auth = useAuth()
  const isAdmin = auth.user?.role === 'admin'

  logger.debug(`useIsAdmin: result=${isAdmin}`, { userRole: auth.user?.role })
  return isAdmin
}

/**
 * Hook that requires authentication and redirects if not authenticated
 * Useful for ensuring user is logged in before accessing a component
 * @returns Authenticated user or null if loading
 */
export function useRequireAuth() {
  logger.debug(`useRequireAuth: enforcing authentication requirement`)

  const auth = useAuth()
  const navigate = useNavigate()

  if (!auth.isLoading && !auth.isAuthenticated) {
    logger.warn(`useRequireAuth: user not authenticated, redirecting to login`)
    navigate(ROUTES.LOGIN, { replace: true })
    return null
  }

  if (auth.isLoading) {
    logger.debug(`useRequireAuth: auth still loading`)
    return null
  }

  logger.debug(`useRequireAuth: user is authenticated`, { userId: auth.user?.id })
  return auth.user
}

/**
 * Hook that requires admin authentication
 * Redirects to home if user is not admin
 * @returns Boolean indicating admin access granted
 */
export function useRequireAdmin(): boolean {
  logger.debug(`useRequireAdmin: enforcing admin requirement`)

  const auth = useAuth()
  const navigate = useNavigate()

  if (!auth.isLoading && !auth.isAuthenticated) {
    logger.warn(`useRequireAdmin: user not authenticated, redirecting to login`)
    navigate(ROUTES.LOGIN, { replace: true })
    return false
  }

  if (!auth.isLoading && auth.isAuthenticated && auth.user?.role !== 'admin') {
    logger.warn(`useRequireAdmin: user is not admin, redirecting home`, { userRole: auth.user?.role })
    navigate(ROUTES.HOME, { replace: true })
    return false
  }

  if (auth.isLoading) {
    logger.debug(`useRequireAdmin: auth still loading`)
    return false
  }

  logger.debug(`useRequireAdmin: admin access granted`, { userId: auth.user?.id })
  return true
}
